/**
 * ADR-0046 phase 2b: the person card — where a person stops being a list
 * of role rows and becomes a record with domains, chips, and (for the
 * identity domain, v1) editable fields.
 *
 * Two modes:
 *  - EDIT: opened from a person's row in Users & Roles. Domains render
 *    with their stewardship chips; identity fields are inputs. Saving a
 *    field in a domain STC doesn't steward triggers THE one interactive
 *    moment: the server refuses with TAKEOVER_REQUIRED, we confirm with
 *    the admin in their own words, and the retry carries the consent —
 *    promotion can neither happen by accident nor be bypassed.
 *  - CREATE: the "create new person" front door (ADR-0046 §6, staff
 *    only) — a thin skin over the existing provision-person action.
 *
 * Chips are information; the confirm is the only control that touches
 * stewardship. All classes reused from the existing admin CSS — no new
 * colour enters the brand inventory.
 */
import { doc, getDoc } from "firebase/firestore";

import { db } from "../firebase";
import { escapeHtml } from "../utils/html";
import {
  callPractitionerRoleAction,
  type UpdatePersonPayload,
} from "./cf-client";
import type { PersonProvenance } from "./loaders";

const STEWARD_LABEL: Record<string, string> = {
  onetouch: "OneTouch",
  stc: "STC",
};

function stewardChip(prov: PersonProvenance | null, domain: string): string {
  const who = prov?.stewards?.[domain];
  if (!who) return "";
  const label = STEWARD_LABEL[who] ?? who;
  const title =
    who === "onetouch"
      ? "maintained by OneTouch sync — editing offers a stewardship take-over"
      : "maintained by STC";
  return `<span class="scope-badge" title="${escapeHtml(title)}">${escapeHtml(label)}</span>`;
}

/** TS twin of person_provenance.primary_name_parts — both name dialects. */
export function primaryNameParts(name: unknown): { given: string; family: string } {
  let entry: Record<string, unknown> | null = null;
  if (Array.isArray(name) && name.length && typeof name[0] === "object" && name[0]) {
    entry = name[0] as Record<string, unknown>;
  } else if (name && typeof name === "object" && "0" in (name as object)) {
    const zero = (name as Record<string, unknown>)["0"];
    if (zero && typeof zero === "object") entry = zero as Record<string, unknown>;
  }
  if (!entry) return { given: "", family: "" };
  const rawGiven = entry.given;
  let given = "";
  if (Array.isArray(rawGiven)) given = String(rawGiven[0] ?? "");
  else if (rawGiven && typeof rawGiven === "object")
    given = String((rawGiven as Record<string, unknown>)["0"] ?? "");
  else given = String(rawGiven ?? "");
  return { given, family: String(entry.family ?? "") };
}

interface OpenPersonCardOptions {
  personDocId: string;
  onSaved: () => void;
}

export async function openPersonCardModal(opts: OpenPersonCardOptions): Promise<void> {
  const snap = await getDoc(doc(db, "_person", opts.personDocId));
  if (!snap.exists()) {
    window.alert("That person record no longer exists.");
    return;
  }
  const data = snap.data() as Record<string, unknown>;
  const prov = (data._provenance ?? null) as PersonProvenance | null;
  const { given, family } = primaryNameParts(data.name);
  const gender = String(data.gender ?? "");
  const birthDate = String(data.birthDate ?? "");
  const telecomCount = Array.isArray(data.telecom)
    ? data.telecom.length
    : data.telecom && typeof data.telecom === "object"
      ? Object.keys(data.telecom).length
      : 0;
  const active = data.active !== false;

  const divergedFields = prov?.diverged?.fields ?? [];
  const divergedBanner = divergedFields.length
    ? `<div class="edit-role-status edit-role-status-error">OneTouch disagrees about: ${escapeHtml(divergedFields.join(", "))} — the sync flagged this rather than overwrite. Saving here keeps STC's value and clears the flag.</div>`
    : "";

  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal-box edit-role-modal" role="dialog" aria-labelledby="personCardTitle">
      <h2 id="personCardTitle">${escapeHtml(`${given} ${family}`.trim() || opts.personDocId)}</h2>
      <p class="edit-role-subject">
        <span class="scope-badge" title="record authored by">${escapeHtml(prov?.origin ?? "unstamped")}</span>
        <code>${escapeHtml(opts.personDocId)}</code>
      </p>
      ${divergedBanner}
      <div class="edit-role-form">
        <div class="edit-role-meta"><strong>Identity</strong> ${stewardChip(prov, "identity")}</div>
        <label class="edit-role-field">
          <span class="edit-role-label">Given name</span>
          <input type="text" id="personCardGiven" value="${escapeHtml(given)}" autocomplete="off" />
        </label>
        <label class="edit-role-field">
          <span class="edit-role-label">Family name</span>
          <input type="text" id="personCardFamily" value="${escapeHtml(family)}" autocomplete="off" />
        </label>
        <label class="edit-role-field">
          <span class="edit-role-label">Gender</span>
          <select id="personCardGender">
            ${["", "female", "male", "other", "unknown"]
              .map((g) => `<option value="${g}" ${g === gender ? "selected" : ""}>${g || "—"}</option>`)
              .join("")}
          </select>
        </label>
        <label class="edit-role-field">
          <span class="edit-role-label">Birth date</span>
          <input type="date" id="personCardBirthDate" value="${escapeHtml(birthDate)}" />
        </label>
        <div class="edit-role-meta"><strong>Contact</strong> ${stewardChip(prov, "contact")}
          <span>${telecomCount} contact entr${telecomCount === 1 ? "y" : "ies"} (editing arrives with 2c)</span>
        </div>
        <div class="edit-role-meta"><strong>Employment</strong> ${stewardChip(prov, "employment")}
          <span class="status-badge status-${active ? "active" : "suspended"}">${active ? "Active" : "Inactive"}</span>
        </div>
        <div class="edit-role-meta"><strong>Access</strong> ${stewardChip(prov, "access")}
          <span>roles &amp; environments — managed in the lists behind this card</span>
        </div>
      </div>
      <div class="edit-role-status" id="personCardStatus" aria-live="polite"></div>
      <div class="modal-actions">
        <button class="btn-outline" id="personCardCancel">Cancel</button>
        <button class="btn-primary" id="personCardSave">Save changes</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.addEventListener("click", (ev) => {
    if (ev.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>("#personCardCancel")?.addEventListener("click", close);

  const status = overlay.querySelector<HTMLElement>("#personCardStatus")!;
  const saveBtn = overlay.querySelector<HTMLButtonElement>("#personCardSave")!;

  saveBtn.addEventListener("click", () => {
    void (async () => {
      const fields: UpdatePersonPayload["fields"] = {};
      const val = (id: string) =>
        overlay.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`)?.value.trim() ?? "";
      const newGiven = val("personCardGiven");
      const newFamily = val("personCardFamily");
      const newGender = val("personCardGender");
      const newBirth = val("personCardBirthDate");
      if (newGiven !== given || newFamily !== family) {
        fields.given = newGiven;
        fields.family = newFamily;
      }
      if (newGender !== gender) fields.gender = newGender;
      if (newBirth !== birthDate) fields.birthDate = newBirth;
      if (Object.keys(fields).length === 0) {
        status.textContent = "Nothing changed.";
        return;
      }

      saveBtn.disabled = true;
      status.textContent = "Saving…";
      const attempt = async (takeOverDomains?: string[]) =>
        callPractitionerRoleAction({
          action: "update-person",
          personDocId: opts.personDocId,
          fields,
          ...(takeOverDomains ? { takeOverDomains } : {}),
        });
      try {
        try {
          await attempt();
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          if (!msg.includes("TAKEOVER_REQUIRED")) throw err;
          // THE one interactive moment (ADR-0046 §5): editing IS the
          // promotion, made deliberate by exactly one confirm.
          const ok = window.confirm(
            "These fields are maintained by OneTouch sync.\n\n" +
              "Take over stewardship of Identity for this person? " +
              "From then on STC owns these fields and the sync will flag " +
              "rather than overwrite them.",
          );
          if (!ok) {
            status.textContent = "Left as OneTouch's — nothing written.";
            saveBtn.disabled = false;
            return;
          }
          await attempt(["identity"]);
        }
        status.textContent = "Saved.";
        opts.onSaved();
        close();
      } catch (err) {
        status.textContent = err instanceof Error ? err.message : "Save failed.";
        saveBtn.disabled = false;
      }
    })();
  });
}

// CREATE mode left this file on 2026-09-16 for the unified "+ Add user"
// door (./add-user). It was only ever half a door: it could attach an
// STC-origin record to a Firebase Auth account that already existed, and
// announced that limit by failing. Creating the record and granting the
// access are one action now, and the environment decides the mechanism.
// EDIT mode — the person card above — stays here.
